import { Injectable, Inject } from '@angular/core';
import {
    IExpenseSummary,
    IExpenseSummaryResponse,
    IDeleteSummaryResponse,
    IDeleteReceiptResponse
} from './IExpenseSummary';
import { Observable, from, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { ITask } from '../lookups/ITask';
import { LoggingService } from '../shared/services/logging/logging.service';
import { tap, map, find } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { IExpenseLine, IExpenseLineResponse } from './IExpenseLine';
import { IExpenseReceipt, IExpenseReceiptResponse } from './IExpenseReceipt';
import { IPaymentMethod } from './IPaymentMethod';
import { ITravelMode } from './ITravelMode';
import { IExpenseTask } from './IExpenseTask';
import { IExpenseItem, IDirectReport } from '../lookups';
import { ITravelRate } from './ITravelRate';
import { ITripLegType } from './ITripLegType';
import { ExpenseStatus, ExpenseRequestTypeEnum } from './expense-enums';
import { UserRoleEnum } from '../user-info';
import { LoadingService } from '../shared/loading';
import { Store } from '@ngrx/store';
import * as fromRoot from '../store/core';
import { UserPaymentMethod } from './UserPaymentMethod';
import {
    IExpenseDetailReceipt,
    IExpenseReportDetailGetRequest,
    IExpenseReportDetailLine
} from './IExpenseReportDetail';

@Injectable({
    providedIn: 'root'
})
export class ExpenseService extends LoadingService {
    private managementUri = `${environment.apiLocation}/ExpenseReportManagementAction`;
    private summaryUri = `${environment.apiLocation}/ExpenseReport`;
    private detailUri = `${environment.apiLocation}/ExpenseReportLine`;
    /** Mobile-aligned report/line APIs (same contracts as eFAACT Mobile). */
    private summaryMobileUri = `${environment.apiLocation}/ExpenseReportSummary`;
    private detailMobileUri = `${environment.apiLocation}/ExpenseReportDetail`;
    private receiptUri = `${environment.apiLocation}/ExpenseReportReceipt`;
    private paymentUri = `${environment.apiLocation}/PaymentMethod`;
    private userPaymentUri = `${environment.apiLocation}/UserPaymentMethod`;
    private travelModeUri = `${environment.apiLocation}/MileageRateType`;
    private expenseTaskUri = `${environment.apiLocation}/ExpenseItemTask`;
    private travelRateUri = `${environment.apiLocation}/TravelRates`;
    private tripLegTypeUri = `${environment.apiLocation}/TripLegType`;
    private expenseDirectReportUri = `${environment.apiLocation}/DirectReportforExpenseReport`;
    private lookupDirectReportUri = `${environment.apiLocation}/DirectReport`;
    private pdobUri = `${environment.apiLocation}/ExpenseLinePdbo`;

    constructor(
        private http: HttpClient,
        protected log: LoggingService,
        protected rootStore: Store<fromRoot.CoreState>
    ) {
        super(rootStore);
    }
    deleteExpense(summary: IExpenseSummary): Observable<IExpenseSummary> {
        this.log.debug({
            type: 'ExpenseService:deleteExpense',
            summary,
            uri: this.summaryUri
        });

        super.Load();
        return this.http
            .request<IDeleteSummaryResponse>('DELETE', this.summaryUri, {
                body: { EntityIDs: [summary.ExpReportNbr] }
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:deleteExpense:reponse',
                        data
                    });
                    super.Stop();
                }),
                map(res =>
                    res.Items
                        ? res.Items[0].EntityID === summary.ExpReportNbr
                            ? summary
                            : null
                        : null
                )
            );
    }
    deleteExpenseDetail(detail: IExpenseLine): Observable<IExpenseLine> {
        this.log.debug({
            type: 'ExpenseService:deleteExpenseDetail',
            detail,
            uri: this.detailMobileUri
        });

        // Mobile: PUT api/ExpenseReportDetail with Deleted=true
        // (ef_expensereportdetail_put → ef_expensereportline_del)
        const line: IExpenseReportDetailLine = {
            ExpReportNbr: detail.ExpReportNbr,
            ExpenseID: detail.ExpenseID,
            SequenceId: detail.SequenceId,
            WorkDate: detail.WorkDate
                ? moment(detail.WorkDate).format('MM/DD/YYYY')
                : undefined,
            Purpose: detail.Purpose,
            EIListID: detail.EIListID,
            EmpAuthID: detail.EmpAuthID,
            TravelTypeID: detail.TravelTypeID,
            Amount: detail.Amount,
            row_version: detail.Row_Version,
            Deleted: true
        };

        return this.putExpenseReportDetail(line).pipe(
            map(resp => (resp != null ? detail : null))
        );
    }
    deleteExpenseReceipt(
        receipt: IExpenseReceipt
    ): Observable<IDeleteReceiptResponse> {
        this.log.debug({
            type: 'ExpenseService:deleteExpenseReceipt',
            receipt,
            uri: this.receiptUri
        });

        super.Load();
        return this.http
            .request<IDeleteReceiptResponse>('DELETE', this.receiptUri, {
                body: { EntityIDs: [receipt.Unique_Id] }
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:deleteExpenseReceipt:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    oversignExpense(
        summary: IExpenseSummary
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:oversignExpense',
            summary,
            uri: this.summaryUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.summaryUri, {
                ExpenseReports: [{ ...summary, RequiresOversign: true }]
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:oversignExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    approveExpense(
        summaries: Array<IExpenseSummary>,
        roleId: number
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:approveExpense',
            summary: summaries,
            uri: this.managementUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.managementUri, {
                ExpenseReportManagementActions: summaries.map(s => ({
                    ...s,
                    UserGroupId: roleId,
                    ExpenseReportStatus: ExpenseStatus.Approved
                }))
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:approveExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    unapproveExpense(
        summaries: Array<IExpenseSummary>,
        roleId: number
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:unapproveExpense',
            summary: summaries,
            uri: this.managementUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.managementUri, {
                ExpenseReportManagementActions: summaries.map(s => ({
                    ...s,
                    UserGroupId: roleId,
                    ExpenseReportStatus: ExpenseStatus.Signed
                }))
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:unapproveExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    disapproveExpense(
        summaries: Array<IExpenseSummary>,
        roleId: number
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:disapproveExpense',
            summary: summaries,
            uri: this.managementUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.managementUri, {
                ExpenseReportManagementActions: summaries.map(s => ({
                    ...s,
                    UserGroupId: roleId,
                    ExpenseReportStatus: ExpenseStatus.Disapproved
                }))
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:disapproveExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    convertExpense(
        summary: IExpenseSummary,
        roleId: number
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:convertExpense',
            summaries: summary,
            uri: this.managementUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.summaryUri, {
                ExpenseReports: [
                    {
                        ...summary,
                        UserGroupId: roleId,
                        ERTypeID: ExpenseRequestTypeEnum.Expense
                    }
                ]
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:convertExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    validateExpense(
        summaries: Array<IExpenseSummary>,
        roleId: number
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:validateExpense',
            summaries,
            uri: this.managementUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.managementUri, {
                ExpenseReportManagementActions: summaries.map(s => ({
                    ...s,
                    UserGroupId: roleId,
                    ExpenseReportStatus: ExpenseStatus.Validated
                }))
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:validateExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    unvalidateExpense(
        summaries: Array<IExpenseSummary>,
        roleId: number
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:unvalidateExpense',
            summary: summaries,
            uri: this.managementUri
        });

        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.managementUri, {
                ExpenseReportManagementActions: summaries.map(s => ({
                    ...s,
                    UserGroupId: roleId,
                    ExpenseReportStatus: ExpenseStatus.Signed
                }))
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:unvalidateExpense:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    getExpenseSummary(
        reportNumber: number,
        EmployeeListID: string
    ): Observable<IExpenseSummary> {
        super.Load();
        const params: any = {
            ExpReportNbr: reportNumber
        };
        if (EmployeeListID) {
            params.EmployeeListID = EmployeeListID;
        }
        this.log.verbose({
            type: 'ExpenseService:getExpenseSummary',
            reportNumber,
            EmployeeListID,
            params
        });
        return this.http
            .post<Array<IExpenseSummary>>(this.summaryUri, params)
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getExpenseSummary:response',
                        data
                    });
                    super.Stop();
                }),
                map(x => x[0])
            );
    }
    getExpenseSummaries(params: {
        startDate?: Date;
        endDate?: Date;
        tasks?: Array<ITask>;
        expenseItems?: Array<IExpenseItem>;
        employeeListID?: string;
        /** When set and equal to employeeListID (or employeeListID omitted), use mobile summary API. */
        currentEmployeeListID?: string;
        expenseType?: ExpenseRequestTypeEnum;
    }): Observable<Array<IExpenseSummary>> {
        super.Load();
        const today = new Date();
        const startDate = moment(
            (params && params.startDate) || moment(today).add(-1, 'years')
        ).format('MM/DD/YYYY');
        const endDate = moment(
            (params && params.endDate) || moment(today).add(1, 'years')
        ).format('MM/DD/YYYY');
        const erTypeId =
            (params && params.expenseType) || ExpenseRequestTypeEnum.Expense;

        // Mobile v1.2 ExpenseReportSummary is token-scoped to the signed-in user.
        const isSelfScoped =
            !params?.employeeListID ||
            (!!params.currentEmployeeListID &&
                params.employeeListID === params.currentEmployeeListID);

        if (isSelfScoped) {
            this.log.debug({
                type: 'ExpenseService:getExpenseSummaries:mobile',
                startDate,
                endDate,
                erTypeId,
                uri: this.summaryMobileUri
            });
            return this.http
                .post(
                    this.summaryMobileUri,
                    {
                        StartDate: startDate,
                        EndDate: endDate,
                        ERTypeID: erTypeId
                    },
                    { responseType: 'text' }
                )
                .pipe(
                    map(raw => {
                        const parsed =
                            this.parseMobileJsonPayload<IExpenseSummary[]>(
                                raw
                            );
                        return (parsed || []).map(s => ({
                            ...s,
                            ReportDate: s.ReportDate
                                ? moment(s.ReportDate).toDate()
                                : s.ReportDate
                        }));
                    }),
                    tap(data => {
                        this.log.verbose({
                            type: 'ExpenseService:getExpenseSummaries:mobile:response',
                            data
                        });
                        super.Stop();
                    })
                );
        }

        this.log.debug({
            type: 'ExpenseService:getExpenseSummaries',
            params,
            uri: this.summaryUri
        });
        const request = {
            StartDate: startDate,
            EndDate: endDate,
            Tasks: (params && params.tasks) || [],
            EmployeeListID: params && params.employeeListID,
            ERTypeID: erTypeId
        };

        this.log.debug({ type: 'ExpenseService:getExpenseSummaries', request });
        return this.http
            .post<Array<IExpenseSummary>>(this.summaryUri, request)
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getExpenseSummaries:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    getExpenseLines(ExpReportNbr: number): Observable<Array<IExpenseLine>> {
        this.log.debug({
            type: 'ExpenseService:getExpenseLines',
            ExpReportNbr,
            uri: this.detailMobileUri
        });
        super.Load();
        // Mobile v1.2: POST api/ExpenseReportDetail with { ExpReportNbr }
        return this.getExpenseReportDetail({ ExpReportNbr }).pipe(
            map(lines =>
                (lines || []).map(line => this.mapMobileLineToExpenseLine(line))
            ),
            tap(data => {
                this.log.verbose({
                    type: 'ExpenseService:getExpenseLines:response',
                    data
                });
                super.Stop();
            })
        );
    }
    getExpenseReceipts(
        ExpReportNbr: number
    ): Observable<Array<IExpenseReceipt>> {
        this.log.debug({
            type: 'ExpenseService:getExpenseReceipts',
            uri: this.receiptUri
        });
        super.Load();
        return this.http
            .post<Array<IExpenseReceipt>>(this.receiptUri, {
                ExpenseGroupId: ExpReportNbr
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getExpenseReceipts:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    getExpenseReceipt(
        ExpenseReport: number,
        receiptId: string
    ): Observable<IExpenseReceipt> {
        super.Load();
        this.log.debug({
            type: 'ExpenseService:getExpenseReceipt',
            receiptId,
            uri: this.receiptUri
        });
        return this.getExpenseReceipts(ExpenseReport).pipe(
            tap(() => super.Stop()),
            map(x => x.find(y => y.Unique_Id === receiptId))
        );
    }
    saveExpenseSummary(
        summary: IExpenseSummary
    ): Observable<IExpenseSummaryResponse> {
        this.log.debug({
            type: 'ExpenseService:saveExpenseSummary',
            summary,
            uri: this.summaryUri
        });
        super.Load();
        return this.http
            .put<IExpenseSummaryResponse>(this.summaryUri, {
                ExpenseReports: [summary]
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:saveExpenseSummary:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    saveExpenseDetail(line: IExpenseLine): Observable<IExpenseLineResponse> {
        this.log.debug({
            type: 'ExpenseService:saveExpenseDetail',
            details: line,
            uri: this.detailUri
        });
        super.Load();
        return this.http
            .put<IExpenseLineResponse>(this.detailUri, {
                ExpenseReportLines: [line]
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:saveExpenseDetail:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    saveExpenseReceipt(
        receipts: Array<IExpenseReceipt>
    ): Observable<IExpenseReceiptResponse> {
        super.Load();
        this.log.debug({
            type: 'ExpenseService:saveExpenseReceipt',
            receipts,
            uri: this.receiptUri
        });
        try {
            if (receipts && receipts.length) {
                return this.http
                    .put<IExpenseReceiptResponse>(this.receiptUri, {
                        ExpenseReportReceipts: receipts
                    })
                    .pipe(
                        tap(data => {
                            this.log.verbose({
                                type: 'ExpenseService:saveExpenseReceipt:reponse',
                                data
                            });
                            super.Stop();
                        })
                    );
                // } else {
                //   return of({StatusCode: 0, StatusType: null, ErrorMessages: [], WarningMessages: [], Items: []});
            }
        } catch (e) {
            this.log.error(e);
            super.Stop();
        }
    }
    getPaymentMethods(
        employeeListID: string
    ): Observable<Array<IPaymentMethod>> {
        super.Load();
        return this.http
            .get<Array<IPaymentMethod>>(
                [this.paymentUri, employeeListID].join('/')
            )
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getPaymentMethods:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    getUserPaymentMethods(
        employeeListID: string
    ): Observable<Array<UserPaymentMethod>> {
        super.Load();
        return this.http
            .get<Array<UserPaymentMethod>>(
                `${this.userPaymentUri}/?Employeelistid=${employeeListID}`
            )
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getUserPaymentMethods:reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    getTravelModes(): Observable<Array<ITravelMode>> {
        super.Load();
        return this.http.get<Array<ITravelMode>>(this.travelModeUri).pipe(
            tap(data => {
                this.log.verbose({
                    type: 'ExpenseService:getTravelModes:reponse',
                    data
                });
                super.Stop();
            })
        );
    }
    getExpenseTasks(EmployeeListID: string) {
        super.Load();
        return this.http
            .get<Array<IExpenseTask>>(
                [this.expenseTaskUri, EmployeeListID].join('/')
            )
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService.getExpenseTasks',
                        data
                    });
                    super.Stop();
                })
            );
    }
    getTravelRates(req: ITravelRate) {
        // Match eFAACT Mobile v1.2 TravelRatesRequest (string dates MM/dd/yyyy).
        const body = {
            City: req.City || '',
            Country_Area: req.Country_Area || '',
            Destination: req.Destination || '',
            LocaleID: req.LocaleID || 0,
            Mileage: req.Mileage != null ? req.Mileage : 0,
            MileageTypeID: req.MileageTypeID != null ? req.MileageTypeID : 0,
            PerDiemTypeID: req.PerDiemTypeID != null ? req.PerDiemTypeID : 0,
            State: req.State || '',
            TravelStart: req.TravelStart
                ? moment(req.TravelStart).format('MM/DD/YYYY')
                : '',
            TravelEnd: req.TravelEnd
                ? moment(req.TravelEnd).format('MM/DD/YYYY')
                : '',
            TripLegTypeID: req.TripLegTypeID != null ? req.TripLegTypeID : 0,
            ZipCode: req.ZipCode || ''
        };
        this.log.verbose({
            type: 'ExpenseService:getTravelRates:request',
            req,
            body
        });
        super.Load();
        return this.http.post<Array<ITravelRate>>(this.travelRateUri, body).pipe(
            tap(data => {
                this.log.verbose({
                    type: 'ExpenseService:getTravelRates:reponse',
                    data
                });
                super.Stop();
            })
        );
    }
    getTripLegTypes() {
        super.Load();
        return this.http.get<Array<ITripLegType>>(this.tripLegTypeUri).pipe(
            tap(data => {
                this.log.verbose({
                    type: 'ExpenseService.getTripLegTypes',
                    data
                });
                super.Stop();
            })
        );
    }
    getDirectReports(
        role: UserRoleEnum,
        activeOnly: boolean,
        startDate: Date,
        endDate: Date
    ) {
        super.Load();
        return this.http
            .post<Array<IDirectReport>>(this.expenseDirectReportUri, {
                UserRole: role,
                ActiveOnly: activeOnly,
                StartDate: moment(startDate).isValid()
                    ? moment(startDate).format('MM-DD-YYYY')
                    : null,
                EndDate: moment(endDate).isValid()
                    ? moment(endDate).format('MM-DD-YYYY')
                    : null
            })
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getDirectReports(post):reponse',
                        data
                    });
                    super.Stop();
                })
            );
    }
    putExpensePdbo(pdbo: any) {
        super.Load();
        try {
            this.log.verbose({ type: 'ExpenseService:putExpensePdbo', pdbo });
            return this.http
                .put<any>(this.pdobUri, {
                    JsonString: JSON.stringify(pdbo)
                })
                .pipe(
                    tap(data => {
                        this.log.verbose({
                            type: 'ExpenseService:putExpensePdbo(put):reponse',
                            data
                        });
                    })
                );
        } finally {
            super.Stop();
        }
    }
    getExpensePdbo(id: number) {
        super.Load();
        try {
            this.log.verbose({ type: 'ExpenseService:getExpensePdbo', id });
            return this.http.get<any>(`${this.pdobUri}/${id}`).pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:getExpensePdbo(get):reponse',
                        data
                    });
                })
            );
        } finally {
            super.Stop();
        }
    }

    /**
     * Mobile-aligned line load: POST `api/ExpenseReportDetail` with ExpReportNbr.
     * API returns a double-encoded JSON string of report lines.
     */
    getExpenseReportDetail(
        request: IExpenseReportDetailGetRequest
    ): Observable<IExpenseReportDetailLine[]> {
        this.log.debug({
            type: 'ExpenseService:getExpenseReportDetail',
            request,
            uri: this.detailMobileUri
        });
        super.Load();
        return this.http.post(this.detailMobileUri, request, {
            responseType: 'text'
        }).pipe(
            map(raw => {
                const parsed = this.parseMobileJsonPayload<IExpenseReportDetailLine[]>(
                    raw
                );
                return parsed || [];
            }),
            tap(data => {
                this.log.verbose({
                    type: 'ExpenseService:getExpenseReportDetail:response',
                    data
                });
                super.Stop();
            })
        );
    }

    /**
     * Mobile-aligned line save: PUT `api/ExpenseReportDetail` with JsonString.
     * Creates or updates a report line (and nested receipt/attachments).
     */
    putExpenseReportDetail(
        line: IExpenseReportDetailLine
    ): Observable<string> {
        const payload = { ...line };
        if (payload.TravelTypeID === 4) {
            payload.TravelStartDate = null;
            payload.TravelEndDate = null;
        }
        this.log.debug({
            type: 'ExpenseService:putExpenseReportDetail',
            line: payload,
            uri: this.detailMobileUri
        });
        super.Load();
        return this.http
            .put(
                this.detailMobileUri,
                { JsonString: JSON.stringify(payload) },
                { responseType: 'text' }
            )
            .pipe(
                map(raw => {
                    const parsed = this.parseMobileJsonPayload<string>(raw);
                    return parsed != null ? String(parsed) : raw;
                }),
                tap(data => {
                    this.log.verbose({
                        type: 'ExpenseService:putExpenseReportDetail:response',
                        data
                    });
                    super.Stop();
                })
            );
    }

    /** Mobile APIs often return a JSON string that itself contains JSON. */
    private parseMobileJsonPayload<T>(raw: string): T | null {
        if (raw == null || raw === '') {
            return null;
        }
        try {
            let value: unknown = JSON.parse(raw);
            if (typeof value === 'string') {
                value = JSON.parse(value);
            }
            return value as T;
        } catch (e) {
            this.log.warn({
                type: 'ExpenseService:parseMobileJsonPayload',
                raw,
                e
            });
            return null;
        }
    }

    /** Map eFAACT Mobile v1.2 ReportLine JSON onto Central IExpenseLine. */
    private mapMobileLineToExpenseLine(
        line: IExpenseReportDetailLine
    ): IExpenseLine {
        const toDate = (value: string | Date | null | undefined) => {
            if (value == null || value === '') {
                return undefined;
            }
            const m = moment(value);
            return m.isValid() ? m.toDate() : undefined;
        };
        const receipt = line.expensereceipt;
        const normalizedReceipt = receipt
            ? this.normalizeMobileReceipt(receipt)
            : undefined;
        return {
            ExpReportNbr: line.ExpReportNbr,
            ExpenseID: line.ExpenseID,
            WorkDate: toDate(line.WorkDate),
            Purpose: line.Purpose,
            EIListID: line.EIListID,
            ExpenseItem: line.ExpenseItem,
            EmpAuthID:
                line.EmpAuthID != null ? Number(line.EmpAuthID) : undefined,
            RptTask: line.RptTask || line.TaskLongName,
            Amount: line.Amount,
            TravelTypeID: line.TravelTypeID as any,
            TravelStartDate: toDate(line.TravelStartDate),
            TravelEndDate: toDate(line.TravelEndDate),
            LocaleID: line.LocaleID,
            PerDiemTypeID: line.PerDiemTypeID,
            TripLegTypeID: line.TripLegTypeID,
            City1: line.City1,
            State1: line.State1,
            Zipcode1: line.Zipcode1,
            City2: line.City2,
            State2: line.State2,
            Zipcode2: line.Zipcode2,
            Destination_Location: line.Destination_Location,
            Country_Area: line.Country_Area,
            Mileage: line.Mileage,
            Mileage_Rate: line.Mileage_Rate,
            RateTypeID: line.RateTypeID,
            ModeofTravel: line.ModeOfTravel,
            Rate_Meals: line.Rate_Meals,
            Rate_Lodging: line.Rate_Lodging,
            Line_ReceiptTotal:
                line.Line_ReceiptTotal ?? normalizedReceipt?.ReceiptTotal,
            Line_ReceiptTax: line.Line_ReceiptTax ?? normalizedReceipt?.ReceiptTax,
            Line_Unallowable: line.Line_Unallowable,
            SequenceId: line.SequenceId,
            PDBOstatus: line.PDBOstatus,
            Row_Version: line.row_version || line.Row_Version,
            expensereceipt: normalizedReceipt,
            Receipts: normalizedReceipt
                ? [
                      {
                          ExpenseRptNbr: line.ExpReportNbr,
                          ExpenseID: line.ExpenseID,
                          Sequenceid: line.SequenceId,
                          ReceiptTax: normalizedReceipt.ReceiptTax,
                          ReceiptTotal: normalizedReceipt.ReceiptTotal,
                          ReceiptDescription:
                              normalizedReceipt.ReceiptDescription,
                          PaymentMethod: normalizedReceipt.PaymentMethod,
                          Creditaccountlistid:
                              normalizedReceipt.creditaccountlistid,
                          Unique_Id: normalizedReceipt.unique_id,
                          File_unique_id: normalizedReceipt.file_unique_id,
                          FileName:
                              normalizedReceipt.attachment?.[0]?.Filename
                      }
                  ]
                : undefined
        };
    }

    /** Normalize SQL/mobile JSON casing for nested receipt + attachments. */
    private normalizeMobileReceipt(
        receipt: IExpenseDetailReceipt | any
    ): IExpenseDetailReceipt {
        const attachments = (receipt.attachment ||
            receipt.Attachment ||
            []) as any[];
        return {
            unique_id: receipt.unique_id || receipt.Unique_Id,
            ReceiptTax: receipt.ReceiptTax,
            ReceiptTotal: receipt.ReceiptTotal,
            ReceiptDescription: receipt.ReceiptDescription,
            PaymentMethod: receipt.PaymentMethod,
            creditaccountlistid:
                receipt.creditaccountlistid || receipt.Creditaccountlistid,
            file_unique_id:
                receipt.file_unique_id || receipt.File_unique_id,
            DeleteFlag: receipt.DeleteFlag,
            efcctxndesc: receipt.efcctxndesc,
            attachment: attachments.map(a => {
                const mime =
                    a.Filemimetype || a.filemimetype || a.FileMimeType || '';
                const file = a.File ?? a.file;
                return {
                    Unique_Id: a.Unique_Id || a.Unique_id || a.unique_id,
                    File_Unique_Id:
                        a.File_Unique_Id ||
                        a.File_unique_id ||
                        a.file_unique_id,
                    File: file,
                    Filename: a.Filename || a.filename || a.FileName,
                    Filesize: a.Filesize ?? a.filesize ?? a.FileSize,
                    Filemimetype: mime,
                    IsPDF: String(mime).toLowerCase().includes('pdf'),
                    IsImage: String(mime).toLowerCase().startsWith('image'),
                    deleteable: true
                };
            })
        };
    }
}
